import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  getIndexingStats,
  getQueueItems,
  getRecentLogs,
  getSuccessRateByEngine,
  getDailyIndexingVolume
} from '@/lib/api/search-indexing/queries';
import { testIndexing, retryFailedJobs } from '@/lib/api/search-indexing/mutations';
import { useToast } from '@/hooks/use-toast';

export const useSearchIndexingData = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const stats = useQuery({
    queryKey: ['search-indexing-stats'],
    queryFn: getIndexingStats,
    refetchInterval: 10000 // Refresh every 10 seconds
  });

  const queue = useQuery({
    queryKey: ['search-indexing-queue'],
    queryFn: () => getQueueItems(50),
    refetchInterval: 5000 // Refresh every 5 seconds
  });

  const logs = useQuery({
    queryKey: ['search-indexing-logs'],
    queryFn: () => getRecentLogs(50),
    refetchInterval: 10000
  });

  const successRates = useQuery({
    queryKey: ['search-indexing-success-rates'],
    queryFn: () => getSuccessRateByEngine(7),
    refetchInterval: 30000 // Refresh every 30 seconds
  });

  const dailyVolume = useQuery({
    queryKey: ['search-indexing-daily-volume'],
    queryFn: () => getDailyIndexingVolume(7),
    refetchInterval: 30000
  });

  const testMutation = useMutation({
    mutationFn: testIndexing,
    onSuccess: () => {
      toast({
        title: 'Test indexing queued',
        description: 'The paper has been added to the indexing queue'
      });
      queryClient.invalidateQueries({ queryKey: ['search-indexing-queue'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to queue test',
        description: error.message,
        variant: 'destructive'
      });
    }
  });

  const retryMutation = useMutation({
    mutationFn: retryFailedJobs,
    onSuccess: (data) => {
      toast({
        title: 'Failed jobs requeued',
        description: data.message
      });
      queryClient.invalidateQueries({ queryKey: ['search-indexing-queue'] });
      queryClient.invalidateQueries({ queryKey: ['search-indexing-stats'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to retry jobs',
        description: error.message,
        variant: 'destructive'
      });
    }
  });

  return {
    stats,
    queue,
    logs,
    successRates,
    dailyVolume,
    testIndexing: testMutation.mutate,
    retryFailedJobs: retryMutation.mutate,
    isTestingMeta: testMutation.isPending,
    isRetrying: retryMutation.isPending
  };
};
